import {
    afterRenderEffect,
    Component,
    computed,
    inject,
    Injector,
    linkedSignal,
    OnInit,
    Signal,
    signal,
    untracked,
    ChangeDetectionStrategy,
} from '@angular/core';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { RouterLink } from '@angular/router';
import { KDateFormat, KDateNow } from '@kiwid-app/kiwid-date-typescript';
import { BtnComponent, EmptyComponent, IKLabelType, KLabelComponent } from '@kiwid-app/kiwid-ui-lib-angular';
import { TranslatePipe } from '@ngx-translate/core';
import { DATE_API } from 'app/core/const/date.const';
import { DEFAULT_PAGINATION } from 'app/core/const/pagination.const';
import { ScrollViewDirective } from 'app/core/directive/scroll-view.directive';
import { EOrderRemakeType } from 'app/core/enums/order.enum';
import { formatDate, parseDateWithTime } from 'app/core/helpers/date.helper';
import { UrlHelper } from 'app/core/helpers/url.helper';
import { isLastPage } from 'app/core/helpers/util';
import { ELocalStorage } from 'app/core/models/authentication/authentication';
import { IEmitGetAllNotificationMessageSocket, INotificationMessage } from 'app/core/models/message/notification-message';
import { NotificationMessageSocketService } from 'app/core/services/message/notification-message-socket.service';
import { TimezoneService } from 'app/core/services/timezone/timezone.service';
import { BaseComponent } from 'app/layout/common/base/base.component';
import { locale as english } from 'app/layout/common/messages/clinic-message-notification/i18n/en';
import { locale as spanish } from 'app/layout/common/messages/clinic-message-notification/i18n/es';
import { locale as portuguese } from 'app/layout/common/messages/clinic-message-notification/i18n/pt';
import { SidebarService } from 'app/layout/components/sidebar/sidebar.service';
import { AvatarComponent } from 'ngx-avatars';
import { NgxTippyModule } from 'ngx-tippy-wrapper';
import { first, Subscription } from 'rxjs';

export type IDataClinicMessageNotificationComponent = {};

@Component({
    selector: 'app-clinic-message-notification',
    imports: [
        AvatarComponent,
        KLabelComponent,
        NgxTippyModule,
        MatProgressSpinner,
        ScrollViewDirective,
        BtnComponent,
        TranslatePipe,
        RouterLink,
        EmptyComponent,
    ],
    templateUrl: './clinic-message-notification.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: './clinic-message-notification.component.scss',
})
export class ClinicMessageNotificationComponent extends BaseComponent implements OnInit {
    private readonly injector = inject(Injector);
    private readonly notificationMessageSocketService = inject(NotificationMessageSocketService);
    private readonly sidebarService = inject(SidebarService);
    private readonly timezoneService = inject(TimezoneService);
    private readonly urlHelper = inject(UrlHelper);

    public dataSidebar: Signal<IDataClinicMessageNotificationComponent>;

    protected readonly isLoading = signal(true);
    protected readonly isLoadingPage = signal(true);
    protected readonly isLastPage = signal(false);
    private readonly notifications = signal<INotificationMessage[]>([]);
    private readonly parameterPagination = linkedSignal<Omit<IEmitGetAllNotificationMessageSocket, 'dateStart'>>(() => {
        return {
            unread: this.unread(),
            offset: 0,
            limit: DEFAULT_PAGINATION,
        };
    });
    protected readonly viewNotifications = computed(() => {
        const notifications = this.notifications().sort(
            (a, b) => parseDateWithTime(b.messageCreatedAt).getTime() - parseDateWithTime(a.messageCreatedAt).getTime(),
        );

        return notifications.map((orderNotification) => {
            const today = formatDate(KDateNow(), DATE_API);
            const isToday = today === KDateFormat(parseDateWithTime(orderNotification.messageCreatedAt), DATE_API);

            return {
                key: orderNotification.id,
                routerLink: this.urlHelper.goToOrderDetailChat(orderNotification.id),
                queryParams: null,
                customerName: orderNotification.customer.name,
                hasPatient: !!orderNotification.patient?.name,
                patientName: orderNotification.patient?.name ?? this.translateKey('EMPTY_PATIENT'),
                customerAvatarUrl: orderNotification.customer.avatar?.url,
                orderNumber: orderNotification.number,
                orderRemake: {
                    enable: !!orderNotification.remakeType,
                    text:
                        orderNotification.remakeType === EOrderRemakeType.REMAKE
                            ? this.translateKey('REMAKE_TYPE.REMAKE_INITIAL')
                            : this.translateKey('REMAKE_TYPE.ADJUSTMENT_INITIAL'),
                    tooltip:
                        orderNotification.remakeType === EOrderRemakeType.REMAKE
                            ? this.translateKey('REMAKE_TYPE.REMAKE')
                            : this.translateKey('REMAKE_TYPE.ADJUSTMENT'),
                    classes: orderNotification.remakeType === EOrderRemakeType.REMAKE ? 'bg-ui-error-default' : 'bg-brand-main-darker',
                    content: '',
                },
                date: isToday
                    ? KDateFormat(parseDateWithTime(orderNotification.messageCreatedAt), 'HH:mm')
                    : KDateFormat(parseDateWithTime(orderNotification.messageCreatedAt), this.dateFormat),
                message: orderNotification.hasMedia
                    ? this.translateKey('MEDIA')
                    : orderNotification.mentions?.length > 0
                      ? orderNotification.mentions.reduce(
                            (total, current) =>
                                total.replaceAll(`[${current.id}]`, `<span class="text-brand-main-darker font-semiBold">@${current.name}</span>`),
                            orderNotification.message,
                        )
                      : orderNotification.message,
                badge: orderNotification.totalBadge,
                labelStyle: {
                    type: 'info-light' as IKLabelType,
                    backgroundColor: '#ECF7FF',
                    borderColor: '#ECF7FF',
                    textColor: '#235881',
                },
            };
        });
    });
    protected readonly isLoadingRead = signal(false);
    protected readonly isEmpty = computed(() => !this.isLoadingPage() && this.notifications().length === 0);
    protected readonly unread = linkedSignal(() => {
        const unread = localStorage.getItem(ELocalStorage.K_UNREAD_NOTIFICATION);

        return !unread || unread === 'true';
    });

    ngOnInit(): void {
        this.registerOnTranslate('CLINIC_MESSAGE_NOTIFICATION', portuguese, english, spanish, false);
        this.getData();
    }

    private get dateFormat(): string {
        return this.translateKey('DATE_FORMAT');
    }

    protected close(): void {
        this.sidebarService.close();
    }

    protected markRead(): void {
        if (this.isLoadingRead()) {
            return;
        }

        this.isLoadingRead.set(true);

        this.subscriptions.add(
            this.notificationMessageSocketService
                .emitMarkReadMessageNotification()
                .pipe(first())
                .subscribe({
                    next: () => {
                        this.parameterPagination.update((prev) => ({
                            ...prev,
                            offset: 0,
                        }));
                        this.isLoadingRead.set(false);
                    },
                    error: () => {
                        this.isLoadingRead.set(false);
                    },
                }),
        );
    }

    protected nextPage(): void {
        if (!this.isLoading() && !this.isLoadingPage() && !this.isLastPage()) {
            this.isLoadingPage.set(true);
            this.parameterPagination.update((prev) => ({
                ...prev,
                offset: this.notifications().length,
            }));
        }
    }

    protected toggleUnread(): void {
        this.unread.update((prev) => !prev);
        this.parameterPagination.update((prev) => ({
            ...prev,
            offset: 0,
        }));
    }

    private getData(): void {
        const getAllNotifications = () => {
            let dateStart = new Date().toISOString();
            const subscriptions: Subscription[] = [];

            const runOnSubscribe = () => {
                subscriptions.forEach((subscription) => subscription.unsubscribe());
            };

            const getData = (parameter: Omit<IEmitGetAllNotificationMessageSocket, 'dateStart'>) => {
                runOnSubscribe();

                if (parameter.offset === 0) {
                    dateStart = KDateNow().toISOString();
                    this.isLoading.set(true);
                    this.notifications.set([]);
                }

                this.isLoadingPage.set(true);

                subscriptions.push(
                    this.notificationMessageSocketService
                        .emitGetAllOrderMessage({
                            ...parameter,
                            dateStart,
                        })
                        .pipe(first())
                        .subscribe({
                            next: ({ data, meta }) => {
                                if (meta.pagination.offset === 0) {
                                    this.notifications.set([]);
                                }
                                this.notifications.update((prev) => [...prev, ...data]);
                                this.isLastPage.set(isLastPage(meta));
                                this.isLoadingPage.set(false);
                                this.isLoading.set(false);
                            },
                            error: () => {
                                this.isLastPage.set(true);
                                this.isLoadingPage.set(false);
                                this.isLoading.set(false);
                            },
                        }),
                );
            };

            this.subscriptions.add({
                unsubscribe: () => runOnSubscribe(),
            });

            afterRenderEffect(
                () => {
                    const isConnected = this.notificationMessageSocketService.isConnected();
                    const parameter = this.parameterPagination();

                    untracked(() => {
                        if (isConnected) {
                            getData(parameter);
                        } else {
                            runOnSubscribe();
                            if (parameter.offset !== 0) {
                                this.parameterPagination.update((prev) => ({ ...prev, offset: 0 }));
                            }
                        }
                    });
                },
                {
                    injector: this.injector,
                },
            );
        };

        getAllNotifications();
    }
}
